import React, { memo, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import FastImage from 'utils/packages/rnFastImage';
import LinearGradient from 'utils/packages/linearGradient';
import { rem, Separator } from 'design-system';
import Text from 'design-system/Text';
import { selectHasAUMFeesFeature, selectHasFxFeesFeature, } from 'features/invest/reducers/selectors';
import { benefits, tierThemes } from 'features/premium/benefits';
import { Tier, tierLevelMap } from 'features/premium/entities';
import { selectConnectionsLimit, selectRentReportingAgenciesTierMap, } from 'features/premium/selectors';
import { selectHasRentReporting } from 'features/rent-reporting/selectors';
import { RENT_REPORTING_AGENCIES } from 'features/rent-reporting/utils';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectIsGBUser } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount } from 'utils';
import { selectShouldShowAutosave } from 'features/pots/reducers/selectors';
const CompareFreeAndPremiumTable = ({ premiumTier, showFree = true, }) => {
    const hasRentReporting = useAppSelector(selectHasRentReporting);
    const rentReportingAgenciesTierMap = useAppSelector(selectRentReportingAgenciesTierMap);
    const hasFxFees = useAppSelector(selectHasFxFeesFeature);
    const hasAUMFees = useAppSelector(selectHasAUMFeesFeature);
    const tierFxRateMap = useAppSelector((store) => store.invest.tierFxRateMap);
    const autoInvestFeesMap = useAppSelector((store) => store.invest.autoInvestTierFees);
    const aumFeesMap = useAppSelector((store) => store.invest.tierSecclAUMFeesMap);
    const interestPotsTierRateMap = useAppSelector((store) => store.pots.interestPotsTierRateMap);
    const autosaveFees = useAppSelector((store) => store.pots.savingsPlanTierFees);
    const hasAutomatedSavings = useAppSelector(selectShouldShowAutosave);
    const isGBUser = useAppSelector(selectIsGBUser);
    const connectionsLimitTierMap = useAppSelector(selectConnectionsLimit);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const rows = useMemo(() => {
        const rows = [
            ...(showFree
                ? [
                    {
                        leftValue: tierThemes[Tier.free].shortName,
                        rightValue: (<LinearGradient {...colors.gradients.ultimate} style={styles.premiumHeader}>
                  <Text Text-14 White centered>
                    {tierThemes[premiumTier].shortName}
                  </Text>
                </LinearGradient>),
                        id: 'header',
                    },
                ]
                : []),
            ...(hasRentReporting
                ? [
                    {
                        label: 'Credit building',
                        leftValue: 'N/A',
                        rightValue: rentReportingAgenciesTierMap[premiumTier]?.map((agency) => RENT_REPORTING_AGENCIES[agency] ? (<FastImage key={agency} source={RENT_REPORTING_AGENCIES[agency]
                                .image} style={staticStyles.providerLogo}/>) : null),
                        id: 'rentReporting',
                    },
                ]
                : []),
            {
                label: 'Bank connections',
                leftValue: connectionsLimitTierMap?.FREE?.toString(),
                rightValue: connectionsLimitTierMap?.[premiumTier] === null
                    ? 'Unlimited'
                    : connectionsLimitTierMap?.[premiumTier]?.toString(),
                id: 'connections',
            },
            ...(isGBUser && interestPotsTierRateMap !== undefined
                ? [
                    {
                        label: 'Interest on savings',
                        leftValue: `${interestPotsTierRateMap[Tier.free]}%`,
                        rightValue: `${interestPotsTierRateMap[premiumTier]}%`,
                        id: 'potsInterest',
                    },
                ]
                : []),
            ...(tierLevelMap[benefits.customCategories.minTier] <= // This check is here so we can find references for custom categories easily
                tierLevelMap[premiumTier]
                ? [
                    {
                        label: 'Custom categories & offline accounts',
                        leftValue: 'N/A',
                        rightValue: 'Unlimited',
                        id: 'customCategories',
                    },
                ]
                : []),
            ...(hasFxFees && tierFxRateMap !== undefined
                ? [
                    {
                        label: 'FX fees on your trades',
                        leftValue: `${tierFxRateMap[Tier.free]}%`,
                        rightValue: `${tierFxRateMap[premiumTier]}%`,
                        id: 'fxFees',
                    },
                ]
                : []),
            ...(hasAUMFees && !!aumFeesMap
                ? [
                    {
                        label: 'AUM fee on your assets',
                        leftValue: `${aumFeesMap[Tier.free]}%`,
                        rightValue: `${aumFeesMap[premiumTier]}%`,
                        id: 'aumFees',
                    },
                ]
                : []),
            ...(isGBUser &&
                hasAutomatedSavings &&
                autosaveFees &&
                typeof autosaveFees[Tier.free] === 'number' &&
                typeof autosaveFees[premiumTier] === 'number' &&
                autosaveFees[Tier.free] !== 0
                ? [
                    {
                        label: 'Autosave fee',
                        leftValue: autosaveFees[Tier.free]
                            ? formatAmount(autosaveFees[Tier.free], 'GBP', 2)
                            : 'Free',
                        rightValue: autosaveFees[premiumTier]
                            ? formatAmount(autosaveFees[premiumTier], 'GBP', 2)
                            : 'Free',
                        id: 'automatedSavings',
                    },
                ]
                : []),
            ...(isGBUser &&
                autoInvestFeesMap &&
                typeof autoInvestFeesMap[Tier.free] === 'number' &&
                typeof autoInvestFeesMap[premiumTier] === 'number' &&
                autoInvestFeesMap[Tier.free] !== 0
                ? [
                    {
                        label: 'Autoinvest fee',
                        leftValue: autoInvestFeesMap[Tier.free]
                            ? formatAmount(autoInvestFeesMap[Tier.free], 'GBP', 2)
                            : 'Free',
                        rightValue: autoInvestFeesMap[premiumTier]
                            ? formatAmount(autoInvestFeesMap[premiumTier], 'GBP', 2)
                            : 'Free',
                        id: 'autoInvest',
                    },
                ]
                : []),
        ];
        return rows;
    }, [
        aumFeesMap,
        autoInvestFeesMap,
        autosaveFees,
        colors.gradients.ultimate,
        connectionsLimitTierMap,
        hasAUMFees,
        hasAutomatedSavings,
        hasFxFees,
        hasRentReporting,
        interestPotsTierRateMap,
        isGBUser,
        premiumTier,
        rentReportingAgenciesTierMap,
        showFree,
        styles.premiumHeader,
        tierFxRateMap,
    ]);
    const { width } = useAppFrameDimensions();
    const usableWidth = width - rem(showFree ? 64 : 32);
    const labelFlexBasis = usableWidth * 0.5;
    const columnFlexBasis = usableWidth * (showFree ? 0.25 : 0.5);
    return (<View style={showFree ? styles.container : null}>
      {rows.map((row, index, arr) => (<React.Fragment key={row.label || 'header'}>
          <View style={[styles.row, !showFree && styles.rowWithoutFree]}>
            <View style={[
                showFree ? styles.labelColumn : styles.labelColumnWithoutFree,
                { flexBasis: labelFlexBasis },
            ]}>
              <Text TextThin-14 Secondary numberOfLines={2}>
                {row.label}
              </Text>
            </View>
            {showFree ? (<View style={[styles.column, { flexBasis: columnFlexBasis }]}>
                <Text Text-14 centered>
                  {row.leftValue}
                </Text>
              </View>) : null}
            <View style={[
                showFree ? styles.column : styles.columnWithoutFree,
                { flexBasis: columnFlexBasis },
                showFree && styles.premiumColumnBackground,
                index === 0 && styles.premiumColumnBackgroundFirst,
                index === arr.length - 1 && styles.premiumColumnBackgroundLast,
            ]}>
              {typeof row.rightValue === 'string' ? (<Text Text-14 Primary centered>
                  {row.rightValue}
                </Text>) : (row.rightValue)}
            </View>
          </View>
          {showFree && index < arr.length - 1 && index > 0 ? (<Separator style={styles.separator}/>) : null}
        </React.Fragment>))}
    </View>);
};
const staticStyles = StyleSheet.create({
    providerLogo: {
        width: rem(24),
        height: rem(24),
        marginHorizontal: rem(1),
    },
});
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginBottom: rem(16),
    },
    premiumHeader: {
        width: '100%',
        paddingVertical: rem(3),
        borderRadius: rem(6),
    },
    row: {
        flex: 1,
        flexDirection: 'row',
        paddingHorizontal: rem(32),
    },
    rowWithoutFree: {
        paddingHorizontal: rem(16),
    },
    labelColumn: {
        justifyContent: 'center',
        marginVertical: rem(16),
    },
    labelColumnWithoutFree: {
        justifyContent: 'center',
        paddingVertical: rem(16),
    },
    column: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: rem(16),
    },
    columnWithoutFree: {
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(16),
        justifyContent: 'flex-end',
    },
    separator: {
        backgroundColor: colors.cards.transparentOnDark,
    },
    premiumColumnBackground: {
        backgroundColor: colors.background.dark,
    },
    premiumColumnBackgroundFirst: {
        paddingHorizontal: rem(8),
        borderTopLeftRadius: rem(12),
        borderTopRightRadius: rem(12),
    },
    premiumColumnBackgroundLast: {
        borderBottomLeftRadius: rem(12),
        borderBottomRightRadius: rem(12),
    },
}));
export default memo(CompareFreeAndPremiumTable);
